import type { PracticeClassification } from "../../types";
import { practiceBasisLabel } from "../../practice-lens-ui";

/**
 * Read-only practice classification panel (2W). Settings mounts it inside the
 * curator-capability gate. It carries no write control: this is a pure display
 * of the signal's effective practice membership
 * (get_signal_detail's practice_classification), never actor, reason, version,
 * event id or internal evidence.
 *
 * Three honest states, distinct from each other:
 *  - hydrating: get_signal_detail hasn't landed yet — an honest skeleton, not
 *    a guess at "not yet routed".
 *  - classification === null: routing is unavailable (a degraded read on the
 *    server), rendered distinctly from "not yet routed".
 *  - classification.practices === []: a valid, hydrated read that found no
 *    membership yet.
 *  - classification.practices non-empty: each practice in canonical order,
 *    with an honest basis label. automated_routing stays "not_available" by
 *    ruling, so a basis of "automated" (when it appears) is rendered exactly
 *    as reported — this panel never implies automated routing exists beyond
 *    what the server actually returned.
 */
export function PracticeClassificationPanel({
  classification,
  hydrating,
}: {
  classification: PracticeClassification | null | undefined;
  hydrating: boolean;
}): JSX.Element {
  return (
    <div className="sx-z-practice">
      <p className="sx-zone-title sx-sm">Practice classification</p>
      {hydrating ? (
        <div className="sx-practice-panel-skel" aria-hidden="true">
          <span className="sx-skel" style={{ width: "70%", height: 12 }} />
          <span className="sx-skel" style={{ width: "45%", height: 12 }} />
        </div>
      ) : classification === null || classification === undefined ? (
        <p className="sx-body sx-sm">Practice routing unavailable.</p>
      ) : classification.practices.length === 0 ? (
        <p className="sx-body sx-sm">Not yet routed to a practice.</p>
      ) : (
        <ul className="sx-practice-list">
          {classification.practices.map((p) => (
            <li key={p.practiceKey} className="sx-practice-row">
              <span className="sx-body sx-sm">{p.displayName}</span>
              <span className="sx-meta">{practiceBasisLabel(p.bases)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
